import { X } from 'lucide-react'
import type React from 'react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useAuth } from '../../contexts/AuthContext'
import { useProfile } from '../../hooks/useProfile'
import { theme } from '../../theme/theme'
import { deleteAvatar, getAvatarUrl, updateProfileAvatar, uploadAvatar } from '../../utils/avatarUtils'
import { AvatarUpload } from './AvatarUpload'

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: ${theme.spacing.md};
`

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`

const ModalTitle = styled.h2`
  font-size: ${theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.sm};
  transition: ${theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text};
  }
`

const ModalBody = styled.div`
  padding: ${theme.spacing.xl};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`

const SectionTitle = styled.h3`
  font-size: ${theme.typography.md};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};

  &:last-child {
    border-bottom: none;
  }
`

const InfoLabel = styled.div`
  font-size: ${theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`

const InfoValue = styled.div`
  font-size: ${theme.typography.sm};
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
`

interface ProfileSettingsModalProps {
  isOpen: boolean
  onClose: () => void
}

export const ProfileSettingsModal: React.FC<ProfileSettingsModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth()
  const { profile, refreshProfile } = useProfile()
  const [uploading, setUploading] = useState(false)

  // Get current avatar URL from profile
  const currentAvatarUrl = profile?.avatar_url ? getAvatarUrl(profile.avatar_url) : null

  // Get user's full name
  const fullName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : user?.email || 'User'

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  const handleUpload = async (file: File) => {
    if (!user?.id) {
      return { success: false, error: 'User not authenticated' }
    }

    setUploading(true)

    try {
      // Delete old avatar if exists
      if (profile?.avatar_url) {
        await deleteAvatar(profile.avatar_url)
      }

      // Upload new avatar
      const uploadResult = await uploadAvatar(user.id, file)

      if (!uploadResult.success || !uploadResult.path) {
        return uploadResult
      }

      // Update profile with new avatar path
      const updateResult = await updateProfileAvatar(user.id, uploadResult.path)

      if (!updateResult.success) {
        return { success: false, error: updateResult.error }
      }

      // Refresh profile to get updated data
      await refreshProfile()

      return uploadResult
    } catch (error) {
      console.error('Avatar upload error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload avatar',
      }
    } finally {
      setUploading(false)
    }
  }

  const handleRemove = async () => {
    if (!user?.id || !profile?.avatar_url) {
      return
    }

    setUploading(true)

    try {
      // Delete from storage
      await deleteAvatar(profile.avatar_url)

      // Update profile to remove avatar_url
      await updateProfileAvatar(user.id, '')

      // Refresh profile
      await refreshProfile()
    } catch (error) {
      console.error('Avatar remove error:', error)
      throw error
    } finally {
      setUploading(false)
    }
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!profile) {
    return null
  }

  return (
    <ModalOverlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>User Settings</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <Section>
            <SectionTitle>Profile Picture</SectionTitle>
            <AvatarUpload
              currentAvatarUrl={currentAvatarUrl}
              userName={fullName}
              size={120}
              onUpload={handleUpload}
              onRemove={handleRemove}
              disabled={uploading}
            />
          </Section>

          <Section>
            <SectionTitle>Profile Information</SectionTitle>
            <InfoRow>
              <InfoLabel>Email</InfoLabel>
              <InfoValue>{user?.email}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>First Name</InfoLabel>
              <InfoValue>{profile.first_name || '-'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Last Name</InfoLabel>
              <InfoValue>{profile.last_name || '-'}</InfoValue>
            </InfoRow>
            {profile.birthday && (
              <InfoRow>
                <InfoLabel>Birthday</InfoLabel>
                <InfoValue>{new Date(profile.birthday).toLocaleDateString()}</InfoValue>
              </InfoRow>
            )}
            {profile.gender && (
              <InfoRow>
                <InfoLabel>Gender</InfoLabel>
                <InfoValue>{profile.gender}</InfoValue>
              </InfoRow>
            )}
          </Section>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  )
}
